import { useTranslation } from "react-i18next";
import { useMediaQuery, glyphs, fonts, colors } from "../../Styles";
import { useParams } from "react-router";
import { useAppSelector } from "../../Store";
import { useEffect, useMemo, useState } from "react";
import { Graph_QL } from "../../Services";
import { useSystem } from "../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { ButtonList, NewInput, NewSelect } from "../BaseComponents";
import { NewFieldStyles } from "../../Definitions";
import { Setting } from "../Settings";

const SYSTEM_ID = 1;

export default function LfSettings() {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 570px)`);
  const responsiveBox = useMediaQuery(`(max-width: 1420px)`);

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : SYSTEM_ID;

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getSystemCall, data: systemsState } = useSystem(connection);

  useEffect(() => {
    getSystemCall({ ids: [systemId] });
  }, []);

  // Get System Name for Header
  const system = useMemo(() => {
    return systemsState[domain]?.data.find(
      (system) => system.systemId === systemId
    );
  }, [domain, systemId, systemsState]);
  
  // #region | Daily Rotation
  const [dailyRotation, setDailyRotation] = useState<number>(0);

  const dailyRotationOptions = [
    { label: t("on", { defaultValue: "on" }), id: 0 },
    { label: t("off", { defaultValue: "off" }), id: 1 },
  ];
  // #endregion

  // #region | Feed Balance
  const [unit, setUnit] = useState<number>(0);

  const unitOptions = [
    { label: t("units.kg", { defaultValue: "kg" }), id: 0 },
    { label: t("units.pct", { defaultValue: "pct" }), id: 1 },
  ];
  // #endregion

  return (  <div style={{display: "flex", gap: "20px", alignItems: mobileXs ? "center" : "end", justifyContent: "center", flexDirection: "column", width: !responsiveBox ? "1050px" : "100%"}}>
      
    {/* Daily Rotation */}
    <Setting header={t("locationFeederPages.settings.dailyRotation", { defaultValue: "Daily Rotation" })}
      startEnhancer={glyphs.Setting({ size: "25px" })}
      content={ <div style={{ display: "flex", flexDirection: "column", alignItems: mobileXs ? "center" : "start" }} >
        <NewSelect
          options={dailyRotationOptions}
          onChange={(e) => setDailyRotation(Number(e?.id ?? 0))}
          fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
          valueOverrides={{ defaultValue: dailyRotationOptions[0] }}
        />
        <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, marginTop: "5px", textAlign: mobileXs ? "center" : "left" }} >
          {t("locationFeederPages.settings.dailyRotationDisclaimer", { defaultValue: "NB: If the system is turned off a daily rotation will make sure to maintain the motor." })}
        </p>
      </div> }
    />

    {/* Feed in Silos */}
    <Setting
      header={t("locationFeederPages.settings.feedInSilos", { defaultValue: "Feed in Silos" })}
      startEnhancer={glyphs.Setting({ size: "25px" })}
      content={<></>}
    />

    {/* Feed Speed */}
    <Setting
      header={t("feed.feedCurve.feedSpeed", { defaultValue: "feed speed" })}
      startEnhancer={glyphs.Setting({ size: "25px" })}
      content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
        <NewInput
          header={t("feed.feedCurve.minFeedSpeed", { defaultValue: "minimum feed speed (g/min)" })}
          fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
          valueOverrides={{ defaultValue: system?.min_feed_speed }}
          placeholder={`${t("units.g", { defaultValue: "g" })}/${t("units.min", { defaultValue: "min" })}`}
        />
        <NewInput
          header={t("feed.feedCurve.maxFeedSpeed", { defaultValue: "maximum feed speed (g/min)" })}
          fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
          valueOverrides={{ defaultValue: system?.max_feed_speed }}
          placeholder={`${t("units.g", { defaultValue: "g" })}/${t( "units.min", { defaultValue: "min" } )}`}
        />
      </div> }
    />

    {/* Feed Balance */}
    <Setting
      header={t("feed.general.feedBalance", { defaultValue: "Feed Balance" })}
      startEnhancer={glyphs.Condition({ size: "25px" })}
      content={ <div style={{ display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start" }} >
        <NewSelect
          header={t("locationFeederPages.settings.units", { defaultValue: "Units" })}
          options={unitOptions}
          onChange={(e) => setUnit(Number(e?.id ?? 0))}
          fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
          valueOverrides={{ defaultValue: unitOptions[0] }}
        />
        <NewInput
          header={t("locationFeederPages.settings.attentionLimit", { defaultValue: "Attention Limit" }) + (unit === 0 ? ` (${t("units.kg", { defaultValue: "KG" })})` : ` (${t("units.pct", { defaultValue: "PCT" })})`) }
          fieldOverrides={{ fieldStyle: NewFieldStyles.greyTertiary }}
          placeholder={ unit === 0 ? t("units.kg", { defaultValue: "KG" }) : t("units.pct", { defaultValue: "PCT" }) }
        />
      </div> }
    />

    {/* Save/discard btns */}
    <ButtonList 
      contents={[t("globalSettingsPages.globalSettings.saveChanges", {defaultValue: "save changes"}), t("globalSettingsPages.globalSettings.discardChanges", {defaultValue: "discard changes"})]} 
      onClick={(i => {
        if (i === 0) {}
        else if (i === 1) {}
      })}
      overrides={{
        baseButton: {width: "fit-content", height: "fit-content", padding: "10px", backgroundColor: colors.greyPrimary},
        hoverStyles: {backgroundColor: colors.greySecondary},
        endEnhancerStyles: {marginLeft: "5px"}
      }}      
    />
  </div> );
}
